import { useContext, useEffect, useState } from 'react';
import { Fade, Stack, Chip, Typography, Button, InputAdornment, Box, OutlinedInput } from '@mui/material';
import { SearchIcon } from 'lucide-react';
import { MetrcLogo, metrcTagsClasses, roomDetailClasses, tableClasses, DetailAndDelBtn } from '../../utility';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import VehicleEntryPopup from '../Popup/VehicleEntryPopup';
import DeleteAlertBox from '../../utility/messages/DeleteAlertBox';

const vehicleKeyTitleMap = [
  { key: 'vehicle_make', title: 'Make' },
  { key: 'vehicle_model', title: 'Model' },
  { key: 'vehicle_license_plate_number', title: 'License Plate' },
];

export default function _VehiclesTab({ currDarkThemeClass, farmContextValue, triggerAddNewVehicle, handleSetValues }) {
  const feedbackCX = useContext(FeedbackContext);

  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [detailChildren, setDetailChildren] = useState(null);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  useEffect(() => {
    if (farmContextValue && Array.isArray(farmContextValue.farms) && farmContextValue.farms.length > 0) {
      setFarms(farmContextValue.farms);
      const defaultFarm = farmContextValue.farms[0];
      setSelectedFarm(defaultFarm);
      setSelectedFilter(defaultFarm.farm_id);
    }
  }, [farmContextValue]);

  useEffect(() => {
    if (triggerAddNewVehicle) {
      handleAddNewVehicle();
      handleSetValues('triggerAddNewVehicle', false);
    }
  }, [triggerAddNewVehicle, handleSetValues]);

  const handlePopupClose = () => {
    setDetailChildren(null);
    setOpen(false);
  };

  const handleSetSelVehicle = (prop, value) => {
    setDetailChildren(state => ({
      ...state,
      selVehicle: {
        ...state?.selVehicle,
        [prop]: value,
      },
    }));
  };

  const formatData = items =>
    items.map((item, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      ...vehicleKeyTitleMap.map(({ key }) => item[key] ?? 'N/A'),
      <DetailAndDelBtn key={`action-${item.id}`} {...getVehicleActionBtnProps(item)} />,
    ]);

  const fetchVehicles = async () => {
    if (!selectedFarm) return;

    setLoading(true);
    try {
      let url = `transporters/vehicles?license_number=${encodeURIComponent(selectedFarm.license_number)}&limit=${rowsPerPage}&page=${page + 1}`;

      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const results = response.data.results || response.data || [];
        setData(formatData(results));
        setTotalCount(response.data.count ?? results.length ?? 0);
      }
    } catch (error) {
      console.error('Error fetching vehicles', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error fetching vehicles.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [selectedFarm, page, rowsPerPage, searchTerm]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleFilterChange = farmId => {
    const farmObj = farms.find(f => f.farm_id === farmId) || null;
    setSelectedFarm(farmObj);
    setSelectedFilter(farmId);
    setPage(0);
  };

  const sChipsProps = farm => ({
    key: farm.farm_id,
    label: farm.farm_name,
    variant: selectedFilter === farm.farm_id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.farm_id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const metrcTagFarmChipsList = (
    <Stack direction="row" spacing={1}>
      {farms.length > 0 ? farms.map(farm => <Chip key={farm.farm_id} {...sChipsProps(farm)} />) : <Typography>No farms available</Typography>}
    </Stack>
  );

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };

  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') event.preventDefault();
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search by Make or Model',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon size={16} />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  const handleSyncToMetrc = async () => {
    if (!selectedFarm) return;
    setSyncLoading(true);

    const url = `transporters/vehicles?license_number=${encodeURIComponent(selectedFarm.license_number)}&sync_to_metrc=true`;

    try {
      const response = await apiService.fetchInstance(url);
      if (response?.status === 200) {
        await fetchVehicles();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Vehicles synced from Metrc successfully.',
        });
      }
    } catch (error) {
      console.error('Error syncing vehicles from Metrc', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: error?.response?.data?.error || 'Error syncing vehicles from Metrc.',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleEntityEntryModify = async vehicle => {
    try {
      const vehicleData = {
        ...vehicle,
        farm: vehicle.farm || selectedFarm.farm_id,
      };
      await apiService.modifyInstance(`transporters/vehicles/${vehicleData.id}`, vehicleData);
      await fetchVehicles();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Vehicle updated successfully.',
      });
      return { success: true };
    } catch (err) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: err?.response?.data?.error || 'Error updating vehicle.',
      });
      return { success: false };
    }
  };

  const handleEntitySave = async vehicle => {
    try {
      const vehicleData = {
        ...vehicle,
        farm: selectedFarm.farm_id,
      };
      await apiService.createInstance('transporters/vehicles', vehicleData);
      await fetchVehicles();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Vehicle created successfully.',
      });
      return { success: true };
    } catch (err) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: err?.response?.data?.error || 'Error creating vehicle.',
      });
      return { success: false };
    }
  };

  const handleVehicleEntryView = vehicle => {
    setOpen(true);
    setDetailChildren({
      selVehicle: { ...vehicle },
      title: `${vehicle.vehicle_make} ${vehicle.vehicle_model}`,
      farmName: selectedFarm?.farm_name,
      toShowSave: true,
      handleVehicleSave: handleEntityEntryModify,
      handleSetSelVehicle,
    });
  };

  const handleAddNewVehicle = () => {
    if (!selectedFarm) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Please select a farm first.',
      });
      return;
    }
    setOpen(true);
    setDetailChildren({
      selVehicle: {
        farm: selectedFarm.farm_id,
      },
      title: 'New Vehicle',
      farmName: selectedFarm?.farm_name,
      toShowSave: true,
      handleVehicleSave: handleEntitySave,
      handleSetSelVehicle,
    });
  };

  const handleDeleteClick = vehicle => {
    setVehicleToDelete(vehicle);
    setDeleteAlertOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!vehicleToDelete) return;

    try {
      const url = `transporters/vehicles/${vehicleToDelete.id}`;
      await apiService.deleteInstance(url);
      await fetchVehicles();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Vehicle deleted successfully.',
      });
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: err?.response?.data?.error || 'Error deleting vehicle.',
      });
    } finally {
      setVehicleToDelete(null);
    }
  };

  const getVehicleActionBtnProps = vehicle => ({
    currDarkThemeClass,
    toShowDelBtn: true,
    sx: { justifyContent: 'flex-start' },
    onDetailClick: () => handleVehicleEntryView(vehicle),
    onDelClick: () => handleDeleteClick(vehicle),
  });

  const tableTitles = [
    { title: 'Sl. No', width: 80 },
    ...vehicleKeyTitleMap.map(e => ({ title: e.title })),
    { title: 'Actions', width: 120, align: 'center' },
  ];

  const displayProps = {
    animationClass: 'vehiclesTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Vehicles Found',
    entity: 'Vehicles',
    tableEntries: data,
    tableTitles,
    toShowAddBtn: false, // Add is triggered via BaseNewAndDetail
    onAddClick: handleAddNewVehicle,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
  };

  const vehicleEntryPopupProps = {
    currDarkThemeClass,
    open,
    onClose: handlePopupClose,
    ...detailChildren,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>
              Show by Farm:
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading || !selectedFarm}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{
                color: '#B1D23A',
                '&.Mui-disabled': { color: '#B1D23A' },
              }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>

        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...displayProps} />
        </Box>

        {open && <VehicleEntryPopup {...vehicleEntryPopupProps} />}

        <DeleteAlertBox
          open={deleteAlertOpen}
          onClose={() => setDeleteAlertOpen(false)}
          onDelete={handleDeleteConfirm}
          currDarkThemeClass={currDarkThemeClass}
          title={`Delete Vehicle: ${vehicleToDelete?.vehicle_make || ''} ${vehicleToDelete?.vehicle_model || ''}`}
          headContentText="This will delete the vehicle from both Metrc and the database. This action cannot be undone."
        />
      </Box>
    </Fade>
  );
}
